/**
 * External dependencies
 */
import type { Maybe, ProductId } from '@nab/types';

/**
 * Internal dependencies.
 */
import { dispatchProductsAddedToCartEvent } from './events';
import { isObject } from '../../../utils/lodash';
import { domReady, log } from '../../../utils/helpers';

domReady( () => {
	if ( ! hasJQuery( window ) ) {
		return;
	}

	const cart = getWooCommerceCart();
	if ( cart ) {
		updateNabCart( cart );
	}

	try {
		window
			.jQuery( document.body )
			.on( 'wc_fragments_loaded wc_fragments_refreshed', () =>
				setTimeout( () => {
					const newCart = getWooCommerceCart();
					if ( newCart ) {
						updateNabCart( newCart );
					}
				}, 1 )
			);

		window
			.jQuery( document.body )
			.on( 'added_to_cart removed_from_cart', ( _, fragments ) => {
				const newCart = hasOwnProperty( fragments, 'nab_cart_info' )
					? fragments.nab_cart_info ?? { items: [] }
					: { items: [] };
				updateNabCart( newCart as WooCommerceCart );
			} );
	} catch ( e ) {
		log( e );
	}
} );

// =======
// HELPERS
// =======

// ------------------------
// Nelio A/B Testing’s Cart
// ------------------------

function updateNabCart( cart: WooCommerceCart ) {
	const previousCart = getNabCart();
	const addedProducts = cart.items.filter(
		( p ) =>
			! previousCart.items.some(
				( pp ) =>
					pp.product_id === p.product_id &&
					pp.variation_id === p.variation_id
			)
	);
	dispatchProductsAddedToCartEvent(
		addedProducts.map( ( p ) => ( {
			id: p.variation_id || p.product_id,
		} ) )
	);

	setNabCart( cart );
}

function getNabCart(): WooCommerceCart {
	const key = getNabCartKey();
	const cart = getWooCommerceCart();
	if ( ! sessionStorage.getItem( key ) && cart ) {
		setNabCart( { items: cart.items } );
	}

	try {
		const data = JSON.parse(
			sessionStorage.getItem( key ) ?? '{}'
		) as Maybe< Partial< WooCommerceCart > >;
		return {
			items: Array.isArray( data?.items ) ? data?.items ?? [] : [],
		};
	} catch ( _ ) {
		return { items: [] };
	}
}

function setNabCart( cart: WooCommerceCart ): void {
	sessionStorage.setItem( getNabCartKey(), JSON.stringify( cart ) );
}

function getNabCartKey(): string {
	return `nab_${ getWooCommerceCartFragmentsParams().fragment_name }`;
}

// ----------------------------
// WooCommerce’s Fragments/Cart
// ----------------------------

function getWooCommerceCart(): Maybe< WooCommerceCart > {
	const fragmentName = getWooCommerceCartFragmentsParams().fragment_name;

	try {
		const wooCommerceFragments = JSON.parse(
			sessionStorage.getItem( fragmentName ) ?? '{}'
		) as Maybe< Partial< WooCommerceFragments > >;
		return wooCommerceFragments?.nab_cart_info;
	} catch ( _ ) {
		return undefined;
	}
}

function getWooCommerceCartFragmentsParams(): WooCommerceCartFragmentsParams {
	const params = hasWooCommerceCartFragmentsParams( window )
		? window.wc_cart_fragments_params
		: {};

	return {
		fragment_name: '',
		...params,
	};
}

function hasWooCommerceCartFragmentsParams( x: unknown ): x is {
	wc_cart_fragments_params: Partial< WooCommerceCartFragmentsParams >;
} {
	return (
		hasOwnProperty( x, 'wc_cart_fragments_params' ) &&
		isObject( x.wc_cart_fragments_params )
	);
}

// ------
// Others
// ------

function hasJQuery( x?: unknown ): x is { jQuery: JQuery } {
	return hasOwnProperty( x, 'jQuery' ) && 'function' === typeof x.jQuery;
}

function hasOwnProperty< Y extends PropertyKey >(
	obj: unknown,
	prop: Y
): obj is Record< Y, unknown > {
	return !! obj && typeof obj === 'object' && obj.hasOwnProperty( prop );
}

// =====
// TYPES
// =====

type JQuery = ( selector: string | Node ) => JQueryElement;
type JQueryElement = {
	readonly on: (
		event: string,
		callback: ( ...args: unknown[] ) => void
	) => void;
};

type WooCommerceCart = {
	readonly items: ReadonlyArray< WooCommerceCartItem >;
};

export type WooCommerceCartItem = {
	readonly product_id: ProductId;

	readonly variation_id: ProductId;
};

type WooCommerceCartFragmentsParams = {
	readonly fragment_name: string;
};

type WooCommerceFragments = {
	readonly nab_cart_info: WooCommerceCart;
};
